import WebApp from '@twa-dev/sdk'
import { WebAppUser } from '@twa-dev/types'

import { authApiClient, authTokenLocalStorageKey } from '@/lib/api/lib/client'
import { localStorageClient } from '@/lib/db/local/client'
import { AppInitialisation } from '@/domain/Sharing'
import { calculateTimeUntilExpiration, getTokenDeadline } from '@/lib/auth'
import {
    isMarketing,
    parseStartParam,
    toInitState
} from '@/lib/share/startParam'

const tgUserLocalStorageKey = 'tg.user'
export const tgUserLanguage = 'tg.user.language'

class UserValidationError extends Error {
    override name = 'UserValidationError' as const
    constructor(msg?: string) {
        super(msg)
    }
}

export function parseInitState(
    startParam: string | undefined
): AppInitialisation | undefined {
    return toInitState(parseStartParam(startParam))
}

const DEV_USER_ID = 110115
const DEV_USER: WebAppUser = {
    id: DEV_USER_ID,
    is_bot: false,
    first_name: 'First name',
    last_name: 'last_name',
    username: 'test local user',
    language_code: 'en_us',
    is_premium: true,
    photo_url: 'https://ui-avatars.com/api/?name=John+Doe'
}

/** Align with worker buffer (TOKEN_EXPIRATION_BUFFER = 600s) */
const TOKEN_REFRESH_THRESHOLD_SECONDS = 600

function storeToken(token: string | undefined): void {
    if (!token) {
        throw new UserValidationError('Received empty token from exchange')
    }
    localStorage.setItem(authTokenLocalStorageKey, token)
}

function storeTgUser(tgUser: Record<string, unknown>): void {
    localStorage.setItem(tgUserLocalStorageKey, JSON.stringify(tgUser))
}

export async function tgUserLoader() {
    const searchParams = new URLSearchParams(window.location.search)
    const needReAuth = searchParams.get('reauth')?.toLowerCase() === 'true'
    const token = localStorage.getItem(authTokenLocalStorageKey)
    const startParam = parseStartParam(WebApp.initDataUnsafe.start_param)
    // A tracked link (campaign, referral, short link) is reported by the
    // exchange, which reads the signed start parameter: exchange even when
    // the token is still fresh.
    const needExchange =
        needReAuth ||
        isMarketing(startParam) ||
        !token ||
        calculateTimeUntilExpiration(getTokenDeadline(token)) <=
            TOKEN_REFRESH_THRESHOLD_SECONDS

    WebApp.MainButton.hide()
    WebApp.BackButton.hide()

    WebApp.enableClosingConfirmation()
    WebApp.ready()

    if (!WebApp.isExpanded) WebApp.expand()

    const initData = toInitState(startParam)
    if (initData) {
        await localStorageClient.saveInitState(initData)
    }

    const u = WebApp.initDataUnsafe.user
    if (u && typeof u === 'object' && 'id' in u) {
        const user = u as WebAppUser
        const tgUser = {
            tgId: user.id.toString(),
            username: user.username || '',
            languageCode: user.language_code || '',
            isPremium: user.is_premium,
            photoUrl: user.photo_url || ''
        }
        storeTgUser(tgUser)
        if (needExchange) {
            try {
                const exchanged = await authApiClient.exchange({
                    v1AuthExchangeRequest: {
                        initData: WebApp.initData
                    }
                })
                storeToken(exchanged.token)
                if (needReAuth) {
                    searchParams.set('reauth', 'false')
                    window.history.replaceState(
                        null,
                        '',
                        `${window.location.pathname}?${searchParams.toString()}`
                    )
                }
            } catch (error) {
                console.error('Token exchange failed:', error)
                const stillValid =
                    token &&
                    calculateTimeUntilExpiration(getTokenDeadline(token)) > 0
                if (!stillValid) {
                    // Stale initData or backend down and no usable token:
                    // render the recovery screen instead of crashing the route.
                    localStorage.removeItem(authTokenLocalStorageKey)
                    return undefined
                }
                // The current token is still valid — keep the session going
                // and let the next load retry the refresh.
            }
        }

        return user
    }

    if (import.meta.env.DEV) {
        if (needExchange) {
            const exchanged = await authApiClient.exchange({
                v1AuthExchangeRequest: {
                    initData: import.meta.env.VITE_DEV_USER_INIT_DATA
                }
            })
            storeToken(exchanged.token)
            storeTgUser({
                tgId: DEV_USER_ID.toString(),
                username: DEV_USER.username,
                languageCode: DEV_USER.language_code,
                isPremium: DEV_USER.is_premium,
                photoUrl: DEV_USER.photo_url
            })
        }
        return DEV_USER
    }

    return undefined
}
